<h2 mat-dialog-title>Draw: {{ data.title }}</h2>
<mat-dialog-content class="cm-overlay-drawing-content">
    <div class="cm-hint">Click to add points, click the curve to insert, drag points to move.</div>

    @let size = $canvasSizePx();
    @let ticks = $rulerTicks();
    @let hoverPx = $hoverPx();
    @let hoverCm = $hoverCm();
    <div #canvasArea class="cm-canvas-area">
        <div class="cm-canvas-frame">
            <div class="cm-ruler-corner"></div>

            <svg class="cm-ruler" [attr.width]="size.width" [attr.height]="topRulerHeightPx">
                @for (tick of ticks.x; track $index) {
                    <line
                        class="cm-ruler-tick"
                        [attr.x1]="tick.posPx"
                        [attr.x2]="tick.posPx"
                        [attr.y1]="topRulerHeightPx - (tick.major ? 10 : 4)"
                        [attr.y2]="topRulerHeightPx"
                    />
                    @if (tick.major) {
                        <text class="cm-ruler-label" [attr.x]="tick.posPx + 2" y="10" text-anchor="start" dominant-baseline="middle">{{ tick.label }}</text>
                    }
                }
                @if (hoverPx && hoverCm) {
                    <line class="cm-ruler-marker" [attr.x1]="hoverPx.x" [attr.x2]="hoverPx.x" y1="0" [attr.y2]="topRulerHeightPx" />
                    <rect class="cm-ruler-marker-box" [attr.x]="hoverPx.x + 2" y="3" [attr.width]="hoverCm.x.length * 6 + 4" height="14" />
                    <text class="cm-ruler-marker-label" [attr.x]="hoverPx.x + 4" y="10" text-anchor="start" dominant-baseline="middle">{{ hoverCm.x }}</text>
                }
            </svg>

            <svg class="cm-ruler" [attr.width]="leftRulerWidthPx" [attr.height]="size.height">
                @for (tick of ticks.y; track $index) {
                    <line
                        class="cm-ruler-tick"
                        [attr.x1]="leftRulerWidthPx - (tick.major ? 10 : 4)"
                        [attr.x2]="leftRulerWidthPx"
                        [attr.y1]="tick.posPx"
                        [attr.y2]="tick.posPx"
                    />
                    @if (tick.major) {
                        <text class="cm-ruler-label" [attr.x]="leftRulerWidthPx - 12" [attr.y]="tick.posPx" text-anchor="end" dominant-baseline="middle">
                            {{ tick.label }}
                        </text>
                    }
                }
                @if (hoverPx && hoverCm) {
                    <line class="cm-ruler-marker" x1="0" [attr.x2]="leftRulerWidthPx" [attr.y1]="hoverPx.y" [attr.y2]="hoverPx.y" />
                    <rect class="cm-ruler-marker-box" x="0" [attr.y]="hoverPx.y - 7" [attr.width]="leftRulerWidthPx - 2" height="14" />
                    <text class="cm-ruler-marker-label" [attr.x]="leftRulerWidthPx - 4" [attr.y]="hoverPx.y" text-anchor="end" dominant-baseline="middle">
                        {{ hoverCm.y }}
                    </text>
                }
            </svg>

            <div #canvasCell class="cm-canvas-cell" (mousemove)="handleCanvasMouseMove($event)" (mouseleave)="handleCanvasMouseLeave()">
                <cm-spline-curve
                    [controlPoints]="$splineControlPoints()"
                    [curvePoints]="$splineCurvePoints()"
                    [width]="size.width"
                    [height]="size.height"
                    [controlPointRadius]="controlPointRadiusPx"
                    [controlPointColor]="controlPointColor"
                    [(selectedControlPoint)]="$selectedControlPoint"
                    [(hoveredControlPoint)]="$hoveredControlPoint"
                    (onBeginControlPointDrag)="handleBeginControlPointDrag()"
                    (onBackgroundMouseDown)="handleBackgroundMouseDown()"
                    (onBackgroundClicked)="handleBackgroundClicked($event)"
                    (onSplineClicked)="handleSplineClicked($event)"
                    (onControlPointChanged)="handleControlPointChanged($event.pointId, $event.point)"
                >
                    <canvas cm-background #previewCanvas class="cm-preview-canvas"></canvas>
                </cm-spline-curve>
                @if (hoverPx) {
                    <svg class="cm-guides" [attr.width]="size.width" [attr.height]="size.height">
                        <line class="cm-guide" x1="0" [attr.x2]="hoverPx.x" [attr.y1]="hoverPx.y" [attr.y2]="hoverPx.y" />
                        <line class="cm-guide" [attr.x1]="hoverPx.x" [attr.x2]="hoverPx.x" y1="0" [attr.y2]="hoverPx.y" />
                    </svg>
                }
            </div>
        </div>
    </div>

    <div class="cm-controls">
        <label>
            Width (cm)
            <input type="number" min="1" [value]="$widthCm()" [disabled]="data.sizeFromMattress" (change)="setPositiveNumber('width', $event)" />
        </label>
        <label>
            Height (cm)
            <input type="number" min="1" [value]="$heightCm()" [disabled]="data.sizeFromMattress" (change)="setPositiveNumber('height', $event)" />
        </label>
        <label>
            Stroke width (cm)
            <input type="number" min="0.1" step="0.1" [value]="$strokeWidthCm()" (change)="setPositiveNumber('strokeWidth', $event)" />
        </label>
        <label>
            Color
            <input type="color" [value]="$colorHex()" (change)="setColor($event)" />
        </label>
        <label>
            Shift curve (cm)
            <input type="number" step="0.5" [value]="$shiftCm()" (change)="setShift($event)" />
        </label>
        <button mat-button [disabled]="$controlPoints().length === 0" (click)="shiftCurve()">Shift</button>
        <button mat-button [disabled]="$controlPoints().length === 0" (click)="mirrorCurve('x')">Mirror H</button>
        <button mat-button [disabled]="$controlPoints().length === 0" (click)="mirrorCurve('y')">Mirror V</button>
        @if (data.sources.length > 0) {
            <label>
                Copy from
                <select (change)="setCopySource($event)">
                    @for (source of data.sources; track $index) {
                        <option [value]="$index" [selected]="$index === $copySourceIndex()">{{ source.label }}</option>
                    }
                </select>
            </label>
            <button mat-button (click)="copyFromSource()">Copy</button>
        }
        @let selectedPointCm = $selectedControlPointCm();
        <label>
            Point X (cm)
            <input
                type="number"
                min="0"
                [max]="$widthCm()"
                step="0.1"
                [value]="selectedPointCm?.x ?? ''"
                [disabled]="!selectedPointCm"
                (focus)="handleControlPointCoordinateFocus()"
                (change)="setSelectedControlPointCoordinate('x', $event)"
            />
        </label>
        <label>
            Point Y (cm)
            <input
                type="number"
                min="0"
                [max]="$heightCm()"
                step="0.1"
                [value]="selectedPointCm?.y ?? ''"
                [disabled]="!selectedPointCm"
                (focus)="handleControlPointCoordinateFocus()"
                (change)="setSelectedControlPointCoordinate('y', $event)"
            />
        </label>
        <button mat-button [disabled]="$selectedControlPoint() === undefined" (click)="deleteSelectedControlPoint()">Delete point</button>
        <button mat-button [disabled]="$controlPoints().length === 0" (click)="clearCurve()">Clear curve</button>
    </div>
</mat-dialog-content>
<mat-dialog-actions align="end">
    <button mat-button (click)="apply()">Apply</button>
    <button mat-button (click)="applyAndClose()">Apply &amp; close</button>
    <button mat-button (click)="cancel()">Close</button>
</mat-dialog-actions>
